<template>
    <div class="subscribers-container">
        <!-- Header Section -->
        <div class="page-header">
            <h1 class="page-title">ניהול אנשי קשר</h1>

            <div class="action-buttons">
                <v-btn v-if="selectedSubscribes.length > 0" color="error" class="action-btn" elevation="2"
                    @click="openBulkDeleteDialog()">
                    <v-icon left>mdi-delete-sweep</v-icon>
                    מחק {{ selectedSubscribes.length }} נבחרים
                </v-btn>

                <v-btn color="primary" class="action-btn" elevation="2" @click="openCreateDIalog()">
                    <v-icon left>mdi-account-plus</v-icon>
                    הוספת איש קשר
                </v-btn>

                <v-btn color="secondary" class="action-btn" elevation="2" @click="importDialog = true">
                    <v-icon left>mdi-file-import</v-icon>
                    ייבוא מאקסל
                </v-btn>

                <v-btn color="accent" class="action-btn" elevation="2" @click="sendCallToAllDialog = true">
                    <v-icon left>mdi-phone-outgoing</v-icon>
                    צינתוק לכולם
                </v-btn>
            </div>
        </div>

        <!-- Search and Stats Bar -->
        <div class="search-stats-bar">
            <div class="search-wrapper">
                <v-text-field v-model="search" prepend-inner-icon="mdi-magnify" label="חיפוש לפי שם או טלפון"
                    single-line hide-details filled rounded dense class="search-field" clearable></v-text-field>
            </div>

            <div class="stats-wrapper">
                <div class="stats-item">
                    <v-icon color="primary" class="stats-icon">mdi-account-group</v-icon>
                    <span class="stats-text">סך הכל: <strong>{{ subscribes.length }}</strong> אנשי קשר</span>
                </div>

                <!-- <v-btn color="info" class="export-btn" elevation="1" @click="exportSubscribers()">
                    <v-icon left>mdi-file-export</v-icon>
                    ייצוא לאקסל
                </v-btn> -->
            </div>
        </div>

        <!-- Table Section -->
        <div class="table-container">
            <v-card class="table-card">
                <v-data-table id="table1" :headers="headers" :items="subscribes" :search="search"
                    :loading="progressShow" :items-per-page="itemsPerPage" v-model="selectedSubscribes" show-select
                    item-key="_id" :footer-props="{
                        'items-per-page-options': [10, 20, 50, 100, 500, -1],
                        'items-per-page-text': 'שורות בעמוד:',
                        'show-current-page': true,
                        'show-first-last-page': true
                    }" sort-by="createdAt" sort-desc class="subscribers-table" :mobile-breakpoint="0">
                    <!-- Loading Progress -->
                    <template v-slot:progress>
                        <v-progress-linear color="primary" indeterminate height="3"></v-progress-linear>
                    </template>

                    <!-- select-all header checkbox -->
                    <template v-slot:header.data-table-select="{ on, props }">
                        <v-simple-checkbox v-bind="props" v-on="on" color="primary"></v-simple-checkbox>
                    </template>

                    <!-- Custom Headers -->
                    <template v-slot:[`header.actions`]="{ header }">
                        <span class="table-header-text">פעולות</span>
                    </template>

                    <template v-for="header in headers.filter(h => h.value !== 'actions')"
                        v-slot:[`header.${header.value}`]="{ header }">
                        <span class="table-header-text">{{ header.text }}</span>
                    </template>

                    <!-- Desktop View -->
                    <template v-slot:item="{ item, index, isSelected, select }">
                        <tr class="table-row"
                            :class="{ 'highlighted-row': index % 2 === 0, 'selected-row': isSelected }">
                            <td class="text-center">
                                <v-simple-checkbox :value="isSelected" @input="select($event)"
                                    color="primary"></v-simple-checkbox>
                            </td>
                            <td class="text-center">{{ index + 1 }}</td>
                            <td>
                                <div class="name-cell">
                                    <v-avatar size="32" color="primary" class="white--text">
                                        {{ getInitials(item.name) }}
                                    </v-avatar>
                                    <span class="mr-2">{{ item.name || 'ללא שם' }}</span>
                                </div>
                            </td>
                            <td>
                                <div class="phone-cell">
                                    <v-icon small color="grey darken-1" class="ml-1">mdi-phone</v-icon>
                                    {{ formatPhone(item.phone) }}
                                </div>
                            </td>
                            <td>{{ item.added_by }}</td>
                            <td>
                                <v-chip small :color="getRoleColor(item.role)" text-color="white" class="role-chip">
                                    {{ getRoleName(item.role) }}
                                </v-chip>
                            </td>
                            <td>
                                <div class="date-cell">
                                    <v-icon small color="grey darken-1" class="ml-1">mdi-calendar</v-icon>
                                    {{ item.createdAt }}
                                </div>
                            </td>
                            <td>
                                <div class="date-cell">
                                    <v-icon small color="grey darken-1" class="ml-1">mdi-phone-in-talk</v-icon>
                                    {{ item.last_call || 'אין נתונים' }}
                                </div>
                            </td>
                            <td>
                                <v-btn small text color="primary" @click="openSettingsDialog(item, index)"
                                    class="settings-btn">
                                    <v-icon small left>mdi-cog</v-icon>
                                    הגדרות
                                </v-btn>
                            </td>
                            <td>
                                <div class="actions-cell">
                                    <v-tooltip bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn :disabled="item.role === 'owner'" icon small color="info"
                                                v-bind="attrs" v-on="on" @click="openEditDialog(item, index)"
                                                class="action-icon-btn">
                                                <v-icon small>mdi-pencil</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>עריכה</span>
                                    </v-tooltip>

                                    <v-tooltip bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn :disabled="item.role === 'owner'" icon small color="error"
                                                v-bind="attrs" v-on="on" @click="openDeleteDialog(item, index)"
                                                class="action-icon-btn">
                                                <v-icon small>mdi-delete</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>מחיקה</span>
                                    </v-tooltip>
                                </div>
                            </td>
                        </tr>
                    </template>

                    <!-- Empty State -->
                    <template v-slot:no-data>
                        <div class="empty-state">
                            <v-icon size="64" color="grey lighten-1">mdi-account-off</v-icon>
                            <h3 class="empty-title">אין אנשי קשר</h3>
                            <p class="empty-subtitle">לא נמצאו אנשי קשר במערכת</p>
                            <v-btn color="primary" @click="refreshDetails()">
                                <v-icon left>mdi-refresh</v-icon>
                                רענן נתונים
                            </v-btn>
                        </div>
                    </template>

                    <!-- No Results -->
                    <template v-slot:no-results>
                        <div class="empty-state">
                            <v-icon size="64" color="grey lighten-1">mdi-file-search</v-icon>
                            <h3 class="empty-title">אין תוצאות</h3>
                            <p class="empty-subtitle">לא נמצאו תוצאות עבור "{{ search }}"</p>
                            <v-btn text color="primary" @click="search = ''">
                                נקה חיפוש
                            </v-btn>
                        </div>
                    </template>
                </v-data-table>
            </v-card>
        </div>

        <ImportDialog v-model="importDialog" v-if="importDialog" @exportExampleExcel="exportExampleExcel"
            @checkDuplicate="checkDuplicate" @importedSuccessAdded="importedSuccessAdded" />

        <!-- Dialogs -->
        <DeleteItemDialog v-model="deleteDialog" v-if="deleteDialog" :item="itemToDelete" :items="itemsToDelete"
            :isBulkDelete="isBulkDelete" @itemDeleted="itemDeleted" @itemsDeleted="itemsDeleted" />

        <TzintukSettingsDialog v-model="settingsDialog" v-if="settingsDialog" :item="itemSettings"
            @itemSettingsChange="itemSettingsChange" />

        <SendCallToAllDialog v-model="sendCallToAllDialog" v-if="sendCallToAllDialog" />

        <AddEditDialog v-model="addEditDialog" v-if="addEditDialog" :formTitle="formTitle" :itemToEdit="editedItem"
            @subscribeCreated="newSubscribeSaved" @subscribeEdited="editedSubscribeSaved" />

        <!-- Snackbar -->
        <SnackBar v-model="snackbar" :snacbarText="snacbarText" :snackbarColorBt="snackbarColorBt" />
    </div>
</template>

<script>
import ApiServices from '@/services/api.service'
import SnackBar from '@/components/widgets/snackBar.vue'
import utils from '@/util/utils'
import Auth from '@/services/auth.service.js'
import DeleteItemDialog from '@/components/subscribesList/dialogs/DeleteItemDialog'
import TzintukSettingsDialog from '@/components/subscribesList/dialogs/TzintukSettingsDialog'
import AddEditDialog from '@/components/subscribesList/dialogs/AddEditDialog'
import SendCallToAllDialog from '@/components/subscribesList/dialogs/SendCallToAllDialog'
import TableToExcel from "@linways/table-to-excel"
import * as XLSX from 'xlsx/xlsx.mjs'
import ImportDialog from '@/components/subscribesList/dialogs/ImportDialog'

export default {
    components: {
        SnackBar,
        DeleteItemDialog,
        AddEditDialog,
        ImportDialog,
        TzintukSettingsDialog,
        SendCallToAllDialog
    },
    data: () => ({
        snackbar: false,
        snackbarColorBt: "green",
        snacbarText: "",
        lineNumber: "",
        progressShow: false,
        addEditDialog: false,
        settingsDialog: false,
        importDialog: false,
        importedFailed: [],
        importedSuccess: [],
        formTitle: "",
        itemToDelete: {},
        itemsToDelete: [],
        isBulkDelete: false,
        selectedSubscribes: [],
        editedItem: {},
        itemSettings: {},
        deleteDialog: false,
        sendCallToAllDialog: false,
        search: "",
        subscribes: [],
        headers: [
            { text: '#', value: "index", sortable: false, width: '50px' },
            { text: 'שם', align: 'start', value: 'name' },
            { text: 'טלפון', value: 'phone' },
            { text: 'באמצעות', value: 'added_by' },
            { text: 'הרשאות', value: 'role' },
            { text: 'התחבר בתאריך', value: 'createdAt' },
            { text: 'התקשרות אחרונה', value: 'last_call' },
            { text: 'צינתוק', value: 'record_name', width: '100px' },
            { text: 'פעולות', value: 'actions', sortable: false, width: '120px' },
        ],
        itemsPerPage: 20,
        roleColors: {
            'manager': 'indigo',
            'user': 'teal',
            'owner': 'blue-grey',
            'default': 'red'
        },
        roleNames: {
            'manager': 'מנהל',
            'user': 'משתמש',
            'owner': 'בעלים'
        }
    }),
    methods: {
        // Utility methods
        getInitials(name) {
            if (!name) return '?';
            return name
                .split(' ')
                .map(word => word[0])
                .join('')
                .toUpperCase()
                .substring(0, 2);
        },

        formatPhone(phone) {
            if (!phone) return '';
            // Format as XXX-XXX-XXXX if 10 digits
            if (phone.length === 10) {
                return `${phone.substring(0, 3)}-${phone.substring(3, 6)}-${phone.substring(6)}`;
            }
            return phone;
        },

        getRoleColor(role) {
            return this.roleColors[role] || this.roleColors.default;
        },
        getRoleName(role) {
            return this.roleNames[role] || role;
        },
        // Data fetching
        async getSubscribes() {
            try {
                this.progressShow = true;
                let token = localStorage.getItem("token");
                let api = process.env.VUE_APP_BASE_URL + `/subscribes/get_subscribes_list`;

                const res = await fetch(api, ApiServices.requestOptions("GET", "", token));

                if (!res.ok) {
                    throw new Error(`HTTP error! Status: ${res.status}`);
                }

                const jsonObject = await res.json();
                this.subscribes = jsonObject;
                this.formatDetails();
            } catch (error) {
                console.error("Error fetching subscribers:", error);
                this.showSnackBar(`שגיאה בטעינת נתונים: ${error.message}`, "error");
            } finally {
                this.progressShow = false;
            }
        },

        formatDetails() {
            this.subscribes = this.subscribes.map((subscribe) => {
                return {
                    ...subscribe,
                    createdAt: utils.getCurrentDate(subscribe.createdAt),
                    last_call: subscribe.last_call ? utils.getCurrentDate(subscribe.last_call) : null,
                    added_by: subscribe.added_by === "WEBSITE" ? "אתר הניהול" : subscribe.added_by === "AUTO" ? "אוטומטי" : subscribe.added_by
                };
            });
        },

        // UI interaction methods
        showSnackBar(message, color) {
            this.snacbarText = message;
            this.snackbarColorBt = color;
            this.snackbar = true;
        },

        openDeleteDialog(item, index) {
            this.itemToDelete = { ...item };
            this.isBulkDelete = false;
            this.deleteDialog = true;
        },

        openBulkDeleteDialog() {
            // owners can't be deleted - filter them out defensively, mirrors the disabled delete icon per row
            this.itemsToDelete = this.selectedSubscribes.filter(item => item.role !== 'owner').map(item => ({ ...item }));
            this.isBulkDelete = true;
            this.deleteDialog = true;
        },

        openEditDialog(item, index) {
            this.formTitle = "ערוך איש קשר";
            this.editedItem = { ...item, itemIndex: index };
            this.addEditDialog = true;
        },

        openSettingsDialog(item, index) {
            this.itemSettings = { ...item };
            this.settingsDialog = true;
        },

        openCreateDIalog() {
            this.formTitle = "הוספת איש קשר חדש";
            this.editedItem = {};
            this.addEditDialog = true;
        },

        // CRUD operations callbacks
        newSubscribeSaved(item, text, color) {
            this.showSnackBar(text, color);
            if (item) {
                // Format the new item before adding to the list
                const formattedItem = {
                    ...item,
                    createdAt: utils.getCurrentDate(item.createdAt),
                    last_call: item.last_call ? utils.getCurrentDate(item.last_call) : null,
                    added_by: item.added_by === "WEBSITE" ? "אתר הניהול" : item.added_by
                };

                this.subscribes = [formattedItem, ...this.subscribes];
            }
        },

        editedSubscribeSaved(item, text, color) {
            this.showSnackBar(text, color);
            if (item) {
                const index = this.subscribes.findIndex(subscribe => subscribe._id === item._id);
                if (index !== -1) {
                    // Format the edited item
                    const formattedItem = {
                        ...item,
                        createdAt: utils.getCurrentDate(item.createdAt),
                        last_call: item.last_call ? utils.getCurrentDate(item.last_call) : null,
                        added_by: item.added_by === "WEBSITE" ? "אתר הניהול" : item.added_by
                    };

                    this.$set(this.subscribes, index, formattedItem);
                }
            }
        },

        itemDeleted(id, text, color) {
            this.showSnackBar(text, color);
            if (id) {
                const index = this.subscribes.findIndex(subscribe => subscribe._id === id);
                if (index !== -1) {
                    this.subscribes.splice(index, 1);
                }
            }
        },

        itemsDeleted(ids, text, color) {
            this.showSnackBar(text, color);
            if (ids && ids.length > 0) {
                ids.forEach(id => {
                    const index = this.subscribes.findIndex(subscribe => subscribe._id === id);
                    if (index !== -1) {
                        this.subscribes.splice(index, 1);
                    }
                });
            }
            this.selectedSubscribes = [];
        },

        itemSettingsChange(item, text, color) {
            this.showSnackBar(text, color);
            if (item) {
                const index = this.subscribes.findIndex(subscribe => subscribe._id === item._id);
                if (index !== -1) {
                    this.$set(this.subscribes, index, { ...this.subscribes[index], ...item });
                }
            }
        },

        // Excel operations
        async exportExampleExcel() {
            try {
                // Create a workbook with a single worksheet
                const wb = XLSX.utils.book_new();

                // Sample data for the example
                const exampleData = [
                    { name: 'ישראל ישראלי', phone: '0501234567', callerid_country_code: 'IL', recieve_calls: "כן" },
                    { name: 'דוד כהן', phone: '0521234567', callerid_country_code: 'IL', recieve_calls: "כן" }
                ];

                // Convert the data to a worksheet
                const ws = XLSX.utils.json_to_sheet(exampleData);

                // Add the worksheet to the workbook
                XLSX.utils.book_append_sheet(wb, ws, 'דוגמא');

                // Generate the Excel file and trigger download
                XLSX.writeFile(wb, 'example_subscribers.xlsx');

                this.showSnackBar('קובץ דוגמא הורד בהצלחה', 'success');
            } catch (error) {
                console.error('Error generating example Excel:', error);
                this.showSnackBar('שגיאה ביצירת קובץ דוגמא', 'error');
            }
        },

        async exportSubscribers() {
            try {
                this.progressShow = true;

                // Create a copy of the data for export, excluding unnecessary fields
                const exportData = this.subscribes.map((item, index) => ({
                    '#': index + 1,
                    'שם': item.name,
                    'טלפון': item.phone,
                    'באמצעות': item.added_by,
                    'הרשאות': item.role,
                    'התחבר בתאריך': item.createdAt,
                    'התקשרות אחרונה': item.last_call || 'אין נתונים'
                }));

                // Create a workbook with a single worksheet
                const wb = XLSX.utils.book_new();
                const ws = XLSX.utils.json_to_sheet(exportData);

                // Add the worksheet to the workbook
                XLSX.utils.book_append_sheet(wb, ws, 'אנשי קשר');

                // Generate the Excel file and trigger download
                XLSX.writeFile(wb, `subscribers_${new Date().toISOString().split('T')[0]}.xlsx`);

                this.showSnackBar('הנתונים יוצאו בהצלחה', 'success');
            } catch (error) {
                console.error('Error exporting subscribers:', error);
                this.showSnackBar('שגיאה בייצוא הנתונים', 'error');
            } finally {
                this.progressShow = false;
            }
        },

        ImportExcel(file) {
            // Reset arrays for new import
            this.importedSuccess = [];
            this.importedFailed = [];

            const reader = new FileReader();

            reader.onload = (e) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: 'array' });
                    const worksheet = workbook.Sheets[workbook.SheetNames[0]];
                    const jsonData = XLSX.utils.sheet_to_json(worksheet);

                    // Process each row
                    jsonData.forEach(row => {
                        let { name, phone, callerid_country_code, role } = row;

                        // Skip empty rows
                        if (!phone && !name) {
                            return;
                        }

                        // Validate phone number
                        if (!phone) {
                            this.importedFailed.push({ phone: 'חסר', reason: 'מספר טלפון חסר' });
                            return;
                        }

                        // Convert to string and format
                        phone = String(phone);

                        // Add leading zero if missing
                        if (!phone.startsWith("1") && !phone.startsWith("0")) {
                            phone = "0" + phone;
                        }

                        // Validate phone length
                        if (phone.length < 9) {
                            this.importedFailed.push({ phone, reason: 'מספר טלפון קצר מדי' });
                            return;
                        }

                        // Validate phone format (numbers only)
                        const numberRegex = /^\d+$/;
                        if (!numberRegex.test(phone)) {
                            this.importedFailed.push({ phone, reason: 'מספר טלפון מכיל תווים לא חוקיים' });
                            return;
                        }

                        // Check for duplicates in existing data
                        const isDuplicate = this.subscribes.some(subscribe => subscribe.phone === phone);
                        if (isDuplicate) {
                            this.importedFailed.push({ phone, reason: 'מספר טלפון כבר קיים במערכת' });
                            return;
                        }

                        // Check for duplicates in current import
                        const isDuplicateInImport = this.importedSuccess.some(item => item.phone === phone);
                        if (isDuplicateInImport) {
                            this.importedFailed.push({ phone, reason: 'מספר טלפון מופיע פעמיים בקובץ' });
                            return;
                        }

                        if (!role || role === 'owner') {
                            role = 'user';
                        }

                        // Add to success list
                        this.importedSuccess.push({
                            name: name || 'ללא שם',
                            phone,
                            role,
                            callerid_country_code: callerid_country_code || 'IL'
                        });
                    });

                    // Show import results dialog
                    this.importMessageDialog = true;

                } catch (error) {
                    console.error('Error parsing Excel file:', error);
                    this.showSnackBar('שגיאה בקריאת קובץ האקסל: ' + error.message, 'error');
                }
            };

            reader.onerror = (e) => {
                this.showSnackBar('שגיאה בקריאת הקובץ: ' + e.target.error, 'error');
            };

            reader.readAsArrayBuffer(file);
        },

        importedSuccessAdded() {
            this.showSnackBar(`${this.importedSuccess.length} אנשי קשר נוספו בהצלחה!`, 'success');
            this.getSubscribes();
        },

        refreshDetails() {
            this.getSubscribes();
        },
        // הוסף מתודה חדשה לבדיקת כפילויות
        checkDuplicate(phone, callback) {
            const isDuplicate = this.subscribes.some(subscribe => subscribe.phone === phone);
            callback(isDuplicate);
        },
    },
    mounted() {
        this.lineNumber = Auth.getLineNumber();
        this.getSubscribes();
    }
}
</script>

<style scoped>
/* Main Container */
.subscribers-container {
    padding: 24px;
    max-width: 1600px;
    margin: 0 auto;
}

/* Header Section */
.page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 32px;
    flex-wrap: wrap;
    gap: 16px;
}

.page-title {
    font-size: 28px;
    font-weight: 700;
    color: #0d2c6d;
    margin: 0;
}

.action-buttons {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
}

.action-btn {
    font-weight: 500;
    border-radius: 8px;
    text-transform: none;
    letter-spacing: 0;
    padding: 0 16px;
    height: 42px;
}

/* Search and Stats Bar */
.search-stats-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    flex-wrap: wrap;
    gap: 16px;
}

.search-wrapper {
    width: 320px;
}

.search-field {
    border-radius: 8px;
}

.stats-wrapper {
    display: flex;
    align-items: center;
    gap: 16px;
}

.stats-item {
    display: flex;
    align-items: center;
    gap: 8px;
}

.stats-icon {
    opacity: 0.8;
}

.stats-text {
    font-size: 16px;
    color: #333;
}

.export-btn {
    text-transform: none;
    letter-spacing: 0;
    border-radius: 8px;
}

/* Table Container */
.table-container {
    margin-bottom: 32px;
}

.table-card {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05) !important;
}

.subscribers-table {
    width: 100%;
}

/* Table Styling */
.table-header-text {
    font-weight: 600;
    font-size: 16px;
    color: #0d2c6d;
}

.table-row {
    height: 60px !important;
    transition: background-color 0.2s;
}

.table-row:hover {
    background-color: rgba(13, 44, 109, 0.05) !important;
}

.highlighted-row {
    background-color: rgba(0, 0, 0, 0.02);
}

.selected-row {
    background-color: rgba(13, 44, 109, 0.08) !important;
}

/* Cell Styling */
.name-cell {
    display: flex;
    align-items: center;
}

.phone-cell,
.date-cell {
    display: flex;
    align-items: center;
}

.role-chip {
    font-size: 12px;
    height: 24px;
}

.settings-btn {
    font-size: 12px;
    text-transform: none;
    letter-spacing: 0;
}

.actions-cell {
    display: flex;
    gap: 8px;
    justify-content: center;
}

.action-icon-btn {
    margin: 0 4px;
}

/* Empty State */
.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 48px 0;
    text-align: center;
}

.empty-title {
    margin-top: 16px;
    font-size: 20px;
    font-weight: 600;
    color: #333;
}

.empty-subtitle {
    margin-top: 8px;
    margin-bottom: 24px;
    font-size: 16px;
    color: #666;
}

/* Responsive Styles */
@media (max-width: 960px) {
    .page-header {
        flex-direction: column;
        align-items: flex-start;
    }

    .search-stats-bar {
        flex-direction: column;
        align-items: stretch;
    }

    .search-wrapper {
        width: 100%;
    }

    .stats-wrapper {
        flex-direction: column;
        align-items: flex-start;
    }

    .action-buttons {
        width: 100%;
    }

    .action-btn {
        flex: 1;
    }
}

@media (max-width: 600px) {
    .subscribers-container {
        padding: 16px;
    }

    .page-title {
        font-size: 24px;
    }

    .action-buttons {
        flex-direction: column;
    }

    .action-btn {
        width: 100%;
    }
}
</style>